import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  TrendingUp,
  Instagram,
  ChevronRight,
  Eye,
} from "lucide-react";

const cases = [
  {
    id: "tech-service",
    sector: "Telefonia · Elettronica · Riparazioni",
    name: "Tech Service",
    objective:
      "Aumentare le riparazioni in negozio e farsi conoscere come riferimento locale per assistenza smartphone e tecnologia.",
    work: [
      "Restyle completo del profilo Instagram",
      "Format ricorrenti su trucchi e riparazioni",
      "Riprese settimanali in store + caption SEO local",
    ],
    result:
      "Luca è diventato uno dei volti più riconoscibili nel settore riparazioni TV e tecnologia, generando fiducia e più richieste.",
    profileLink:
      "https://www.instagram.com/sinergy.tech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    cta: "Guarda il profilo",
    handleLabel: "@sinergy.tech",
    covers: [
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/o1hp9a4a_luca.jpeg",
        label: "Risultati profilo",
        metric: "826K views",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/ldpl7tso_luca%202.jpeg",
        label: "Visualizzazioni IG",
        metric: "+828%",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/oo36rocd_SaveClip.App_497848395_1191438619351234_747896898478550010_n.jpg",
        label: "Io parlo troppo?",
        metric: "Reel",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/7l5yuh1u_SaveClip.App_500447118_1408120780610937_5701092975431944732_n.jpg",
        label: "Promo silenziosa",
        metric: "Reel",
      },
    ],
  },
  {
    id: "vs-shop",
    sector: "Abbigliamento donna · E-commerce",
    name: "VS Shop",
    objective:
      "Crescere su TikTok e portare traffico qualificato al sito e-commerce, valorizzando i nuovi arrivi.",
    work: [
      "Strategia TikTok con format viral-ready",
      "Riprese outfit + try-on settimanali",
      "Setup Instagram Shop e UGC mirati",
    ],
    result:
      "Ora tutti i suoi video hanno almeno 10k visualizzazioni e la sua clientela si è allargata.",
    profileLink:
      "https://www.instagram.com/vs_shop.boutique?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    cta: "Guarda il profilo",
    handleLabel: "@vs_shop.boutique",
    covers: [
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/x4fgf73g_valeria%203.jpeg",
        label: "Collezione invernale",
        metric: "Reel",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/jc1cpz8c_valeria%204.jpeg",
        label: "Chat nella realtà",
        metric: "Reel",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/s11xfujc_valeria.jpeg",
        label: "Risultati profilo",
        metric: "1.6M views",
      },
      {
        image:
          "https://customer-assets.emergentagent.com/job_rimfluences-launch/artifacts/tvaa95qw_valeria%202.jpeg",
        label: "Analitica TikTok",
        metric: "+79.6%",
      },
    ],
  },
  {
    id: "bd95",
    sector: "Barber Shop premium",
    name: "BD95",
    objective:
      "Costruire un’identità social forte e una percezione più premium del barber shop.",
    work: [
      "Branding social + bio ottimizzata per prenotare",
      "Reel cinematografici settimanali in negozio",
      "Pubblicazione coerente su IG, TikTok e Facebook",
    ],
    result:
      "Immagine premium rafforzata e prenotazioni aumentate grazie a contenuti più curati e riconoscibili.",
    profileLink:
      "https://www.instagram.com/bdbrancaccio?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    cta: "Guarda il profilo",
    handleLabel: "@bdbrancaccio",
    covers: [
      {
        image:
          "https://images.pexels.com/photos/33448216/pexels-photo-33448216.jpeg?auto=compress&cs=tinysrgb&w=900",
        label: "Fade cinematografico",
        metric: "Reel",
      },
      {
        image:
          "https://images.pexels.com/photos/1319460/pexels-photo-1319460.jpeg?auto=compress&cs=tinysrgb&w=900",
        label: "Styling in negozio",
        metric: "Reel",
      },
      {
        image:
          "https://images.pexels.com/photos/2061820/pexels-photo-2061820.jpeg?auto=compress&cs=tinysrgb&w=900",
        label: "Atmosfera BD95",
        metric: "Brand",
      },
    ],
  },
  {
    id: "korme",
    sector: "Ristorante · Bistrot · Eventi & Piscina",
    name: "Korme Restaurant — Menu Digitale",
    objective:
      "Creare un menu digitale elegante, ordinato e facile da consultare, capace di valorizzare l’esperienza del locale e semplificare l’accesso alle informazioni per i clienti.",
    work: [
      "Struttura completa del menu digitale",
      "Sezioni pranzo, cena, drink ed eventi",
      "Design coerente con l’identità del locale",
      "Esperienza mobile-first + QR code",
    ],
    result:
      "Il menu digitale ha aumentato la percezione di valore del locale e reso l’esperienza più ordinata, moderna e semplice da gestire.",
    type: "image",
    image:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=80",
    accent: "from-purple-500/25 via-rim/25 to-transparent",
    profileLink: "https://kormemenu.my.canva.site/",
    cta: "Guarda il menu",
  },
];

function CoverCard({ cover, handleLabel, isActive }) {
  return (
    <>
      <img
        src={cover.image}
        alt={cover.label}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30" />

      {/* Top badge */}
      <div className="absolute top-2.5 left-2.5 z-10">
        <span className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-md border border-white/25 rounded-full px-2 py-0.5 text-[9px] font-semibold text-white uppercase tracking-wider">
          {cover.metric}
        </span>
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-2.5 right-2.5 z-10">
          <span className="inline-flex items-center gap-1 bg-rim text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg">
            <Eye className="h-2.5 w-2.5" />
            LIVE
          </span>
        </div>
      )}

      {/* Bottom info */}
      <div className="absolute left-2.5 right-2.5 bottom-2.5 z-10 text-white">
        <p className="text-[10px] font-semibold opacity-90">{handleLabel}</p>
        <p className="text-[11px] font-bold leading-tight mt-0.5 line-clamp-2">
          {cover.label}
        </p>
      </div>
    </>
  );
}

function CoverStackDesktop({ covers, handleLabel }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = covers.length;
  const intervalRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setActive((a) => (a + 1) % total);
    }, 4200);
    return () => clearInterval(intervalRef.current);
  }, [paused, total]);

  return (
    <div
      className="relative w-[240px] sm:w-[260px] aspect-[9/16] mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="cover-stack-desktop"
    >
      {covers.map((c, i) => {
        const d = (i - active + total) % total;
        const isActive = d === 0;
        return (
          <motion.button
            key={c.image}
            type="button"
            onClick={() => {
              if (i === active) {
                setActive((a) => (a + 1) % total);
              } else {
                setActive(i);
              }
            }}
            initial={false}
            animate={{
              x: d * 20,
              y: d * 16,
              rotate: d * 3,
              scale: 1 - d * 0.05,
              opacity: isActive ? 1 : Math.max(0.25, 0.75 - (d - 1) * 0.22),
              zIndex: total - d,
            }}
            transition={{ type: "spring", stiffness: 220, damping: 28 }}
            whileHover={
              isActive ? { y: -4 } : { scale: 1 - d * 0.05 + 0.02 }
            }
            style={{ transformOrigin: "left center" }}
            aria-label={`Cover ${i + 1}: ${c.label}`}
            className={`absolute inset-0 rounded-2xl overflow-hidden cursor-pointer text-left border transition-[box-shadow,border-color] duration-500 ${
              isActive
                ? "border-rim/30 shadow-[0_30px_60px_-15px_rgba(124,58,237,0.55),0_0_50px_rgba(168,85,247,0.35)]"
                : "border-white/30 shadow-[0_15px_40px_-15px_rgba(76,29,149,0.4)]"
            }`}
          >
            <CoverCard
              cover={c}
              handleLabel={handleLabel}
              isActive={isActive}
            />
          </motion.button>
        );
      })}

      {/* Dots */}
      <div className="absolute -bottom-7 left-0 right-0 flex justify-center gap-1.5 z-20">
        {covers.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            aria-label={`Vai a cover ${i + 1}`}
            className={`h-1 rounded-full transition-all duration-300 ${
              active === i ? "w-7 bg-rim" : "w-1 bg-rim-ink/25"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function CoverStackMobile({ covers, handleLabel, onCoverClick }) {
  return (
    <div
      data-testid="cover-stack-mobile"
      className="-mx-5 px-5 flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pt-1 pb-2"
      style={{ scrollPaddingLeft: "1.25rem" }}
    >
      {covers.map((c, i) => (
        <button
          key={c.image}
          type="button"
          onClick={() => onCoverClick && onCoverClick(c)}
          aria-label={`Apri cover: ${c.label}`}
          data-testid={`mobile-cover-${i}`}
          className="snap-start shrink-0 w-[130px] aspect-[9/16] relative rounded-2xl overflow-hidden border border-white/30 shadow-[0_15px_40px_-15px_rgba(76,29,149,0.45)] cursor-pointer active:scale-95 transition-transform"
        >
          <CoverCard cover={c} handleLabel={handleLabel} isActive={i === 0} />
        </button>
      ))}
    </div>
  );
}

function Lightbox({ cover, handleLabel, onClose }) {
  React.useEffect(() => {
    if (!cover) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [cover, onClose]);

  if (!cover) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="cover-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/15 backdrop-blur-md border border-white/25 grid place-items-center text-white hover:bg-white/25 transition-colors z-10"
      >
        <span className="text-xl leading-none">×</span>
      </button>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm aspect-[9/16] rounded-3xl overflow-hidden border border-white/20 shadow-[0_40px_80px_-20px_rgba(124,58,237,0.6)]"
      >
        <img
          src={cover.image}
          alt={cover.label}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/90 via-black/40 to-transparent text-white">
          <p className="text-xs font-semibold opacity-80 uppercase tracking-wider">
            {handleLabel}
          </p>
          <p className="mt-1 font-heading text-lg font-bold leading-tight">
            {cover.label}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function PhoneCase({ c, textLeft }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65 }}
      data-testid={`case-card-${c.id}`}
      className="grid md:grid-cols-12 items-center gap-8 md:gap-12"
    >
      <article
        className={`md:col-span-7 bg-rim-surface rounded-3xl p-6 md:p-10 border border-rim/20 shadow-[0_20px_60px_-30px_rgba(124,58,237,0.5)] ${
          textLeft ? "md:order-1" : "md:order-2"
        }`}
      >
        <span className="inline-flex items-center gap-1.5 bg-rim/20 text-rim-glow text-[11px] font-semibold px-3 py-1.5 rounded-full">
          <span className="h-1.5 w-1.5 rounded-full bg-rim-glow" />
          {c.sector}
        </span>

        <h3 className="mt-4 font-heading text-2xl md:text-4xl font-bold tracking-tight leading-tight text-white">
          {c.name}
        </h3>

        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-muted mb-2">
              Obiettivo
            </p>
            <p className="text-rim-muted leading-relaxed text-[14px]">
              {c.objective}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-muted mb-2">
              Cosa abbiamo curato
            </p>
            <ul className="space-y-1.5">
              {c.work.map((w) => (
                <li
                  key={w}
                  className="text-[14px] text-white/80 leading-snug flex gap-2"
                >
                  <span className="text-rim-glow mt-0.5">→</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-rim/15 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-rim/20 grid place-items-center shrink-0">
            <TrendingUp className="h-5 w-5 text-rim-glow" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-muted">
              Risultato
            </p>
            <p className="font-heading text-base md:text-xl font-bold text-rim-glow leading-snug mt-0.5">
              {c.result}
            </p>
          </div>
        </div>

        <a
          href={c.profileLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`case-cta-${c.id}`}
          className="group/btn mt-6 inline-flex items-center gap-2 self-start bg-rim hover:bg-rim-hover text-white text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 hover:shadow-[0_10px_25px_-8px_rgba(168,85,247,0.7)]"
        >
          <Instagram className="h-4 w-4" />
          {c.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:rotate-45" />
        </a>
      </article>

      <div
        className={`md:col-span-5 flex justify-center items-center py-4 md:py-2 ${
          textLeft ? "md:order-2" : "md:order-1"
        }`}
      >
        <div className="relative">
          <div className="absolute -inset-12 rounded-full bg-rim/20 blur-3xl -z-10" />
          <CoverStackDesktop covers={c.covers} handleLabel={c.handleLabel} />
        </div>
      </div>
    </motion.div>
  );
}

function PhoneCaseMobile({ c }) {
  const [lightbox, setLightbox] = useState(null);
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55 }}
      data-testid={`case-card-${c.id}`}
      className="bg-rim-surface rounded-3xl p-5 border border-rim/20 shadow-[0_20px_60px_-30px_rgba(124,58,237,0.5)]"
    >
      <span className="inline-flex items-center gap-1.5 bg-rim/20 text-rim-glow text-[10px] font-semibold px-2.5 py-1 rounded-full">
        <span className="h-1 w-1 rounded-full bg-rim-glow" />
        <span>{c.sector}</span>
      </span>

      <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight leading-tight text-white">
        {c.name}
      </h3>

      <p className="mt-3 text-[13.5px] text-rim-muted leading-relaxed">
        {c.objective}
      </p>

      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted mb-2">
          Cosa abbiamo curato
        </p>
        <ul className="space-y-1.5">
          {c.work.map((w) => (
            <li
              key={w}
              className="text-[13px] text-white/85 leading-snug flex gap-2"
            >
              <span className="text-rim-glow mt-0.5">→</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 mb-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted">
            Contenuti realizzati
          </p>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted/70">
            Scorri
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>
        <CoverStackMobile
          covers={c.covers}
          handleLabel={c.handleLabel}
          onCoverClick={(cover) => setLightbox(cover)}
        />
      </div>

      <div className="mt-5 pt-4 border-t border-rim/15 flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-rim/20 grid place-items-center shrink-0">
          <TrendingUp className="h-4 w-4 text-rim-glow" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rim-muted">
            Risultato
          </p>
          <p className="font-heading text-[15px] font-bold text-rim-glow leading-snug mt-0.5">
            {c.result}
          </p>
        </div>
      </div>

      <a
        href={c.profileLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`case-mobile-cta-${c.id}`}
        className="mt-5 inline-flex items-center gap-1.5 bg-rim hover:bg-rim-hover text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors"
      >
        <Instagram className="h-3.5 w-3.5" />
        {c.cta}
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>

      <Lightbox
        cover={lightbox}
        handleLabel={c.handleLabel}
        onClose={() => setLightbox(null)}
      />
    </motion.article>
  );
}

function ImageCase({ c }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65 }}
      data-testid={`case-card-${c.id}`}
      className="group relative grid md:grid-cols-12 gap-0 bg-white rounded-3xl overflow-hidden border border-black/5 shadow-[0_20px_60px_-30px_rgba(76,29,149,0.3)] hover:shadow-[0_30px_80px_-30px_rgba(124,58,237,0.45)] transition-shadow duration-500"
    >
      <div className="md:col-span-5 relative h-64 md:h-auto md:min-h-[380px] overflow-hidden">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${c.accent} z-10 mix-blend-multiply`}
        />
        <img
          src={c.image}
          alt={c.name}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-5 left-5 z-20">
          <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md text-rim-ink text-[11px] font-semibold px-3 py-1.5 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-rim" />
            {c.sector}
          </span>
        </div>
      </div>

      <div className="md:col-span-7 p-6 md:p-10 flex flex-col">
        <h3 className="font-heading text-2xl md:text-4xl font-bold tracking-tight leading-tight">
          {c.name}
        </h3>

        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-ink/40 mb-2">
              Obiettivo
            </p>
            <p className="text-rim-ink/75 leading-relaxed text-[14px]">
              {c.objective}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-ink/40 mb-2">
              Cosa abbiamo curato
            </p>
            <ul className="space-y-1.5">
              {c.work.map((w) => (
                <li
                  key={w}
                  className="text-[14px] text-rim-ink/75 leading-snug flex gap-2"
                >
                  <span className="text-rim mt-0.5">→</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-black/5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-rim/10 grid place-items-center shrink-0">
            <TrendingUp className="h-5 w-5 text-rim" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-rim-ink/40">
              Risultato
            </p>
            <p className="font-heading text-base md:text-lg font-semibold text-rim-ink leading-snug mt-0.5">
              {c.result}
            </p>
          </div>
        </div>

        <a
          href={c.profileLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`case-cta-${c.id}`}
          className="group/btn mt-6 inline-flex items-center gap-2 self-start bg-rim-ink hover:bg-rim text-white text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 hover:shadow-[0_10px_25px_-8px_rgba(124,58,237,0.6)]"
        >
          {c.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:rotate-45" />
        </a>
      </div>
    </motion.article>
  );
}

export default function CaseStudies() {
  const phoneCases = cases.filter((c) => c.type !== "image");
  const imageCases = cases.filter((c) => c.type === "image");

  return (
    <section
      id="casi-studio"
      data-testid="case-studies-section"
      className="bg-rim-night text-rim-text py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-20">
          <h2
            data-testid="cases-title"
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05] text-balance"
          >
            I clienti che si sono fidati di noi.
          </h2>
          <p className="mt-5 font-heading text-lg md:text-2xl lg:text-3xl font-semibold tracking-tight text-rim-muted leading-snug text-balance max-w-3xl">
            Ogni progetto ha un obiettivo, un metodo e un risultato concreto.
          </p>
        </div>

        {/* Desktop: vertical alternating */}
        <div className="hidden md:block space-y-14 md:space-y-20">
          {phoneCases.map((c, i) => (
            <PhoneCase key={c.id} c={c} textLeft={i % 2 === 0} />
          ))}
          {imageCases.map((c) => (
            <ImageCase key={c.id} c={c} />
          ))}
        </div>

        {/* Mobile: stacked vertically, each with cover scroll */}
        <div className="md:hidden space-y-6">
          {phoneCases.map((c) => (
            <PhoneCaseMobile key={c.id} c={c} />
          ))}
          {imageCases.map((c) => (
            <ImageCase key={c.id} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
}
